import { Box, Link, useDisclosure } from '@chakra-ui/react';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import { isBeforeSeptember, lastYear } from '../../helpers/dates';
import RefusedDocumentsModal from './components/RefusedDocumentsModal';
import locales from './locales';

interface ProofOfAddressSelectTypeProps {
  isSelfHosted: boolean;
  onSelectPoaType: (
    type: 'LANDLINE' | 'INTERNET' | 'ELECTRICITY' | 'INCOME_TAX_NOTICE',
  ) => void;
  onOtherTypeClick: () => void;
}

/**
 * A page asking the user to select the type of proof of address.
 */
const ProofOfAddressSelectType = asBentoPage<ProofOfAddressSelectTypeProps>(
  ({ isSelfHosted, Layout, onPrev, onSelectPoaType }) => {
    const description = isSelfHosted
      ? locales.description.selfHosted
      : locales.description.hosted;

    const detailsModal = useDisclosure();

    return (
      <Layout
        data-testid="proof-of-address-select-type"
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Box>
          <Header description={description} title={locales.title} />

          <SunshineCard.Group isBoxed={false}>
            <SunshineCard
              alignItems="center"
              onClick={() => onSelectPoaType('ELECTRICITY')}
              title={locales.list.ELECTRICITY.title}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="flash" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.list.ELECTRICITY.subtitle}
                </SunshineCard.Text>
              </SunshineCard.Content>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              onClick={() => onSelectPoaType('INTERNET')}
              title={locales.list.INTERNET.title}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="wifi" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.list.INTERNET.subtitle}
                </SunshineCard.Text>
              </SunshineCard.Content>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              onClick={() => onSelectPoaType('LANDLINE')}
              title={locales.list.LANDLINE.title}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="phone" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.list.LANDLINE.subtitle}
                </SunshineCard.Text>
              </SunshineCard.Content>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              onClick={() => onSelectPoaType('INCOME_TAX_NOTICE')}
              title={
                isBeforeSeptember
                  ? locales.list.INCOME_TAX_NOTICE.title
                  : locales.list.INCOME_TAX_NOTICE_AFTER_SEPTEMBER.title
              }
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="file" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
              <SunshineCard.Content>
                <SunshineCard.Text>
                  {locales.formatString(
                    isBeforeSeptember
                      ? locales.list.INCOME_TAX_NOTICE.subtitle
                      : locales.list.INCOME_TAX_NOTICE_AFTER_SEPTEMBER.subtitle,
                    {
                      lastYear: lastYear,
                    },
                  )}
                </SunshineCard.Text>
              </SunshineCard.Content>
            </SunshineCard>
          </SunshineCard.Group>

          <Link marginTop="space-26" onClick={detailsModal.onOpen}>
            {locales.seeRefusedDocuments}
          </Link>

          <RefusedDocumentsModal {...detailsModal} />
        </Box>
      </Layout>
    );
  },
);

export default ProofOfAddressSelectType;
